/**
 * Claim-level support strength from citation refs attached to structured blocks.
 */

import { linkCitationChunk, isIrlCitation, isObservationCitation } from './citationDisplay';
import { groupCitationOrigins, isDatabaseCitation } from './sourceDiversity';

const SUPPORT_BLOCK_TYPES = new Set([
    'executive_summary',
    'key_findings',
    'recommendations',
    'risk_matrix',
    'findings',
    'recommendation_list',
]);

export function normalizeCitationRefList(raw) {
    if (!raw) return [];
    const list = Array.isArray(raw) ? raw : [raw];
    return list
        .map(entry => {
            if (entry == null) return null;
            if (typeof entry === 'number') return String(entry);
            if (typeof entry === 'string') return entry.replace(/[^\d]/g, '') || entry.trim();
            if (typeof entry === 'object') {
                return String(entry.ref ?? entry.citation_ref ?? entry.id ?? '').replace(/[^\d]/g, '');
            }
            return null;
        })
        .filter(Boolean);
}

export function extractBlockCitationRefs(block) {
    if (!block || typeof block !== 'object') return [];
    const refs = [
        ...(Array.isArray(block.citations) ? block.citations : []),
        ...(Array.isArray(block.supporting_refs) ? block.supporting_refs : []),
        ...(Array.isArray(block.refs) ? block.refs : []),
        ...(Array.isArray(block.evidence_refs) ? block.evidence_refs : []),
    ];
    if (Array.isArray(block.items)) {
        for (const item of block.items) {
            if (item && typeof item === 'object') {
                refs.push(...(item.citations || []), ...(item.supporting_refs || []), ...(item.refs || []));
            }
        }
    }
    return normalizeCitationRefList(refs);
}

export function resolveCitationsForRefs(citations = [], chunks = [], refs = []) {
    const normalizedRefs = new Set(normalizeCitationRefList(refs));
    if (!normalizedRefs.size) return [];

    return (Array.isArray(citations) ? citations : []).filter(citation => {
        const ref = String(citation?.ref ?? '').replace(/[^\d]/g, '');
        return ref && normalizedRefs.has(ref);
    });
}

export function inferSupportStrength(citations = [], chunks = [], refs = []) {
    const matched = resolveCitationsForRefs(citations, chunks, refs);
    const sourceCitations = matched.length ? matched : citations;
    const origins = groupCitationOrigins(sourceCitations, chunks);
    const labels = new Set();
    const types = new Set();

    for (const citation of sourceCitations) {
        const linked = linkCitationChunk(citation, chunks);
        const ws =
            citation?.workstream_name ||
            citation?.workstream_repr ||
            linked?.workstream_name ||
            linked?.workstream_repr;
        if (ws) labels.add(String(ws).trim());

        if (isObservationCitation(citation, linked)) types.add('Observation');
        else if (isIrlCitation(citation, linked)) types.add('IRL');
        else if (isDatabaseCitation(citation, linked)) types.add('Database');
        else types.add('Document');
    }

    const sourceCount =
        matched.length ||
        origins.counts.files +
            origins.counts.irls +
            origins.counts.observations +
            origins.counts.databaseEntities;

    let strength = 'limited';
    if (sourceCount >= 6 || types.size >= 3) strength = 'excellent';
    else if (sourceCount >= 3 || types.size >= 2) strength = 'strong';
    else if (sourceCount === 1) strength = 'single';

    return {
        sourceCount,
        workstreams: [...labels].slice(0, 4),
        sourceTypes: [...types],
        strength,
        matchedCitations: matched,
    };
}

export function resolveBlockClaimSupport(block, citations = [], chunks = []) {
    if (!block || typeof block !== 'object') return null;
    const refs = extractBlockCitationRefs(block);
    const support = inferSupportStrength(citations, chunks, refs);

    if (!refs.length && SUPPORT_BLOCK_TYPES.has(String(block.type || '').toLowerCase())) {
        const fallback = inferSupportStrength(citations, chunks, []);
        return fallback.sourceCount > 0
            ? { ...fallback, usesResponseLevel: true }
            : null;
    }

    if (!support.sourceCount) return null;
    return support;
}

export function shouldShowClaimSupport(block) {
    const type = String(block?.type || '')
        .trim()
        .toLowerCase();
    return SUPPORT_BLOCK_TYPES.has(type);
}
